import {
  Component, ElementRef, Input, NgZone, OnInit, Renderer2, ViewChild,
  ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, Optional, EventEmitter, AfterViewInit, TemplateRef
} from '@angular/core';
import {NgbPopover} from '@ng-bootstrap/ng-bootstrap';
import {Subscription} from 'rxjs';

import {MediaCenter} from '@ngmedax/asset';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import {ConfigService} from '@ngmedax/config';
import {ValueService} from '@ngmedax/value';
import {Translatable, TranslationEventService, TranslationService} from '@ngmedax/translation';

import {ComponentMapping, QuestionDisplayChangeInterface} from '../../../types';
import {QuestionnaireMediaCenterService} from '../../services/questionnaire-media-center.service';
import {QuestionnaireStateService} from '../../services/questionnaire-state.service';
import {QuestionnaireEditorService} from '../../services/questionnaire-editor.service';
import {preventMultilineText} from '../../froala/prevent-multiline-text';
import {FroalaCodeViewFlushService} from '../../froala/froala-code-view-flush.service';
import {DomHelperService} from '../../services/dom-helper.service';
import {FROALA_LICENSE_KEY} from '../../licences/froala';
import {QuestionnaireVariablesService} from '../../services/questionnaire-variables.service';
import {LazyFroalaComponent} from '../lazy-froala/lazy-froala.component';
import {TRANSLATION_EDITOR_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';
import {QuestionnaireInspectorService} from '../../services/questionnaire-inspector.service';
import {getEditorLayout, renderWhenNear} from '../../services/editor-layout';
import {flashFocus} from '../../services/focus-flash';


// hack to inject decorator declarations. must occur before class declaration!
export interface QuestionComponent extends Translatable {}

declare const $: any;
declare const window: any;
const Format = Questionnaire.Container.Format;

/**
 * TODO: we should refactor this component. to much code in one file!
 */
@Component({
  selector: 'app-qa-question',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './question.component.html',
  styleUrls: [
    './question.component.css',
    '../../froala/template-tokens.css',
    '../../shared/reusable.css',
    '../../shared/insert-element.css'
  ]
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class QuestionComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() feature = {editor: {patient: {upload: false}}};
  @Input() question: Questionnaire.Container;
  @Input() mapping: ComponentMapping;
  @Input() position: number;
  @Input() pageViewEnabled = false;
  @Input() isFirstOnPage = false;
  @Input() isLastOnPage = false;
  @Input() pages: {startIndex: number; endIndex: number; title: string}[] = [];
  @Input() currentPageIndex = 0;
  public disableInlineVarScope: boolean = true;
  public onChildFocus: EventEmitter<void> = new EventEmitter<void>();
  public froalaInstance: any = null;

  @ViewChild('qaQuestion') questionRef: ElementRef;
  @ViewChild('previewContent') previewContent: ElementRef<HTMLElement>;
  public readonly previewMaxHeight = 280;
  /** Vertical padding of the preview clip that leaves room for the hover fills. */
  public readonly previewClipInset = 12;
  public previewExpanded = false;
  public previewOverflows = false;
  private previewResizeObserver: ResizeObserver;

  @ViewChild('properties', {static: true}) properties: TemplateRef<any>;
  /** classic editor layout: the old card frame around the property template */
  public classicLayout = getEditorLayout(this.config.get('ui.editor.default')) === 'classic';
  /** classic editor layout: the property panel renders once the card comes near the viewport */
  public classicBodyRendered = false;
  private stopNearWatch: () => void = null;
  public activePropertyTab = 'contents';
  public focusUploadVariable = false;
  public previewFocusTarget: string = null;

  /** Clears a completed option focus request so the same field can be selected again. */
  public onPreviewFocusHandled = (): void => {
    this.previewFocusTarget = null;
    this.ref.markForCheck();
  };

  /** Prepares uploads only when enabled by the user, then explains the existing filename field. */
  public onAllowUploadChange(enabled: boolean): void {
    this.question.options.allowUpload = enabled;
    this.focusUploadVariable = enabled;
    if (enabled) {
      this.variables.ensureUploadVariable(this.question, this.locale);
      this.activePropertyTab = 'variables';
    }
    this.state.onUpdateElement.emit({element: null, position: null});
  }
  public activeMediaTab = 'images';
  public readonly mediaTypes = [
    {id: 'images', label: KEYS.EDITOR.IMAGES, icon: 'fa-image'},
    {id: 'videos', label: KEYS.EDITOR.VIDEOS, icon: 'fa-file-video-o'},
    {id: 'audio', label: KEYS.EDITOR.AUDIO, icon: 'fa-file-audio-o'}
  ];

  /** Counts enabled options in the collapsed settings section. */
  public get additionalOptionsCount(): number {
    const options = this.question.options;
    return [options.mark, options.hideInPdf, this.supportsHideInApp && options.hideInApp,
      options.hideInXls, this.feature.editor.patient.upload && options.allowUpload].filter(Boolean).length;
  }

  /** Keeps existing media shortcuts compatible with the grouped media tab. */
  public selectPropertyTab(tab: string): void {
    if (this.mediaTypes.some(media => media.id === tab)) {
      this.activeMediaTab = tab;
      this.activePropertyTab = 'media';
      return;
    }
    this.activePropertyTab = tab;
  }
  private destroyed = false;
  @ViewChild('titleEditor') titleEditor: LazyFroalaComponent;
  public requestedProperty: 'title' | 'description' = null;

  private propertyFocusRequest = 0;
  private pendingPreview: {locale: string; title: string; description: string} = null;
  private previewRefreshTimer: ReturnType<typeof setTimeout> = null;

  /**
   * Froala options for the title editor. Built once in the constructor and kept
   * referentially stable: the froala directive deep clones the options every time
   * it receives a new object reference, which is expensive during change detection.
   */
  public titleEditorOptions: any;
  /**
   * Default froala wysiwyg editor options
   * @type {any}
   */
  public descriptionEditorOptions: any = {
    key: FROALA_LICENSE_KEY,
    immediateAngularModelUpdate: true,
    pluginsEnabled: ['paragraphFormat', 'charCounter', 'codeView', 'wordPaste', 'line_breaker'],
    toolbarButtons: ['bold', 'italic', 'underline', 'paragraphFormat', 'html'],
    placeholderText: 'Beschreibung eingeben...',
    quickInsertButtons: [],
    quickInsertTags: [],
    o: window.CodeMirror,
    codeMirrorOptions: {
      lineNumbers: true,
      mode: {name: 'handlebars', base: 'htmlmixed'}
    },
    charCounterCount: true,
    emoticonsUseImage: false,
    imagePaste: false,
    language: 'de',
    attribution: false,
    lineBreakerOffset: 0,
    pastePlain: true,
    multiLine: true,
  };

  /**
   * Are there any questionnaire variables configured?
   * @type {boolean}
   */
  public hasQuestionnaireVariablesConfig = false;

  /**
   * Can we use the asset module?
   * @type {boolean}
   */
  public hasAssetSupport = false;

  /**
   * Locale for questionnaire. Hardcoded to "de_DE" for now.
   * We need to change this, when we implement multi language support
   * @type {string}
   */
  public locale = 'de_DE';

  /**
   * Subscriptions
   * @type {Subscription[]}
   */
  private subscriptions: Subscription[] = [];

  /**
   * Plugins for extended wysiwyg functionality
   */
  private extendedEditorPlugins = ['paragraphFormat', 'charCounter', 'wordPaste', 'colors', 'align', 'table',
    'lists', 'quote', 'link', 'fontFamily', 'fontSize', 'codeView', 'codeBeautifier', 'specialCharacters', 'emoticons'];

  /**
   * Buttons for extended wysiwyg functionality
   */
  private extendedEditorButtons = {
    moreText: {
      buttons: ['bold', 'italic', 'underline', 'fontFamily', 'fontSize', 'textColor',
        'backgroundColor', 'strikeThrough', 'subscript', 'superscript'],
      align: 'left',
      buttonsVisible: 3
    },

    moreParagraph: {
      buttons: ['paragraphFormat', 'align', 'formatOL', 'formatUL', 'outdent', 'indent', 'quote'],
      align: 'left',
      buttonsVisible: 2
    },

    moreRich: {
      buttons: ['insertLink', 'insertTable', 'emoticons', 'specialCharacters', 'insertHR'],
      align: 'left',
      buttonsVisible: 2
    },

    moreMisc: {
      buttons: ['clearFormatting', 'undo', 'redo', 'html'],
      align: 'right',
      buttonsVisible: 4
    }
  };

  /**
   * Injects dependencies
   */
  public constructor(
    public mediaCenter: QuestionnaireMediaCenterService,
    public inspector: QuestionnaireInspectorService,
    public domHelper: DomHelperService,
    public value: ValueService,
    private editor: QuestionnaireEditorService,
    protected state: QuestionnaireStateService,
    private variables: QuestionnaireVariablesService,
    private config: ConfigService,
    private renderer: Renderer2,
    private ref: ChangeDetectorRef,
    private zone: NgZone,
    private codeViewFlush: FroalaCodeViewFlushService,
    @Optional() protected translationService: TranslationService,
    @Optional() protected translationEventService: TranslationEventService,
  ) {
    this.hasQuestionnaireVariablesConfig
      = !!this.config.get('questionnaire-editor.variable.scopes');

    this.hasAssetSupport = this.mediaCenter.hasAssetSupport();

    // wire description editor instance lifecycle into the code view flush service
    // so that any open code view can be force-synced before save.
    const self = this;
    this.descriptionEditorOptions.events = {
      'initialized': function() {
        self.codeViewFlush.register(this);
      },
      'destroy': function() {
        self.codeViewFlush.unregister(this);
      }
    };

    this.titleEditorOptions = this.buildTitleEditorOptions();

    const updatePlaceholder = () => {
      // choose placeholders based on type (email uses subject/mailtext)
      this.descriptionEditorOptions.placeholderText = this._(this.getDescriptionPlaceholderKey());
      this.titleEditorOptions.placeholderText = this._(this.getTitlePlaceholderKey());
      this.descriptionEditorOptions.language = this.translationService.getLocaleCountryCode(
        this.translationService.getLocale()).toLowerCase();
    };

    updatePlaceholder();
    this.translationEventService && this.translationEventService.onLocaleChanged().subscribe(() => updatePlaceholder());
  }

  /**
   * Runs init code
   */
  public ngOnInit() {
    this.subscriptions.push(this.inspector.selection.subscribe(() => {
      if (!this.selected) {
        this.requestedProperty = null;
        this.previewFocusTarget = null;
      }
      this.ref.markForCheck();
    }));
    this.subscriptions.push(this.state.onUpdateElement.subscribe(change => {
      if (change.element === this.question) {
        this.ref.markForCheck();
      }
    }));

    // enable extended froala editor functions when feature enabled by license
    const isExtendedEditor = !!this.config.get('feature.editor.extendedWysiwyg');

    if (isExtendedEditor) {
      this.descriptionEditorOptions.pluginsEnabled = this.extendedEditorPlugins;
      this.descriptionEditorOptions.toolbarButtons = this.extendedEditorButtons;
    }

    // updates the element in the state service (updates path hashes)
    this.state.updateElement(this.question, this.position);

    // update element on position change
    this.subscriptions.push(this.state.onElementPositionChange.subscribe(() => this.triggerChangeDetection()));

    // get "display change event emitter"
    const questionDisplayChange = this.editor.questionDisplayChange;

    // skips event handling for display change events on "page break" elements
    if (this.question.format !== Questionnaire.Container.Format.PAGE_BREAK) {
      // subscribe to "display change" event
      const subscription = questionDisplayChange.subscribe((displayChange: QuestionDisplayChangeInterface) => {
        for (const cmd of ['expandContents', 'expandDescriptions', 'expandImages', 'expandVideos', 'expandAudio', 'expandConditions', 'expandVariables']) {

          // expands current tab when true
          if (displayChange[cmd]) {
            displayChange.expandQuestion = true;
            this.selectPropertyTab(cmd.replace('expand', '').toLowerCase());
            this.triggerChangeDetection();
          }
        }

        // expands question
        displayChange.expandQuestion && this.domHelper.addClasses(this.questionRef.nativeElement, ['expanded']);

        // folds question
        !displayChange.expandQuestion && this.domHelper.removeClasses(this.questionRef.nativeElement, ['expanded']);
      });

      // add subscription
      this.subscriptions.push(subscription);
    }

    // set title if none found
    !this.question.title && (this.question.title = {[this.locale]: ''});

    // set description if none found
    !this.question.description && (this.question.description = {'type': 'html', 'text': {[this.locale]: ''}});

    // add options if none found
    !this.question.options && (this.question.options = {});

    // set mandatory prop to false if not found
    (typeof this.question.options.mandatory === 'undefined') && (this.question.options.mandatory = false);

    // set active prop to true if not found
    (typeof this.question.options.active === 'undefined') && (this.question.options.active = true);

    // question formats where inline variable scope is allowed
    const allowedForInlineVariables = [
      Format.NUMERIC,
      Format.DATE,
      Format.TEXT,
      Format.DOCUMENT_SCAN,
      Format.SINGLE_CHOICE,
      Format.MULTIPLE_CHOICE,
      Format.MULTI_VALUE
    ];

    // allow inline variable scope, if question format is allowed
    const fmt = this.question && this.question.format;
    (fmt && allowedForInlineVariables.indexOf(fmt) !== -1) && (this.disableInlineVarScope = false);

    // ensure placeholders are correct once question is present
    this.descriptionEditorOptions.placeholderText = this._(this.getDescriptionPlaceholderKey());
    this.titleEditorOptions.placeholderText = this._(this.getTitlePlaceholderKey());
  }

  /**
   * After view init hook to auto-open tabs based on question type
   */
  public ngAfterViewInit() {
    this.observePreviewHeight();
    if (this.classicLayout && this.questionRef) {
      this.stopNearWatch = renderWhenNear(this.questionRef.nativeElement, () => this.zone.run(() => {
        this.classicBodyRendered = true;
        this.ref.markForCheck();
      }));
    }
    if (!this.mapping.sub || (this.question.format === Format.INFO && this.question.extra !== 'email')) {
      this.activePropertyTab = 'descriptions';
    }
    Promise.resolve().then(() => {
      if (!this.destroyed && (this.inspector.requestedQuestion === this.question ||
          (!this.inspector.selection.value && this.position === 0))) {
        this.selectQuestion();
      }
    });
  }

  /** Measures natural content height after text, choices, images or panel widths change. */
  private observePreviewHeight(): void {
    if (!this.previewContent) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.previewResizeObserver = new ResizeObserver(entries => {
        const overflows = entries[0].contentRect.height > this.previewMaxHeight + 1;
        if (this.destroyed || overflows === this.previewOverflows) {
          return;
        }
        this.zone.run(() => {
          this.previewOverflows = overflows;
          if (!overflows) {
            this.previewExpanded = false;
          }
          this.ref.markForCheck();
        });
      });
      this.previewResizeObserver.observe(this.previewContent.nativeElement);
    });
  }

  /** Expands long cards without selecting them or moving focus into the inspector. */
  public togglePreview(event: Event): void {
    event.stopPropagation();
    this.previewExpanded = !this.previewExpanded;
    if (!this.previewExpanded && this.questionRef.nativeElement.getBoundingClientRect().top < 0) {
      this.questionRef.nativeElement.scrollIntoView({block: 'start'});
    }
  }

  /** Opens this card's existing controls in the shared property panel. */
  public selectQuestion(): void {
    if (this.properties) {
      this.inspector.select({question: this.question, template: this.properties});
    }
  }

  /** Queues focus before selecting the question so newly created editors receive it on their first render. */
  public async focusProperty(field: 'title' | 'description' | 'options', event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    const request = ++this.propertyFocusRequest;
    this.requestedProperty = field === 'options' ? null : field;
    this.previewFocusTarget = field === 'options' ? this.getPreviewFocusTarget(event) : null;
    if (field === 'options') {
      this.activePropertyTab = 'contents';
    } else if (field === 'description') {
      this.activePropertyTab = 'descriptions';
    }
    this.ref.markForCheck();
    await this.inspector.select({question: this.question, template: this.properties});
    if (this.destroyed || request !== this.propertyFocusRequest) {
      return;
    }
    if (!this.selected) {
      this.requestedProperty = null;
      this.previewFocusTarget = null;
    } else if (field === 'options' && !this.previewFocusTarget) {
      // no single field was clicked (text, number, date fields etc.): pulse the whole options pane once it rendered
      requestAnimationFrame(() => setTimeout(() => !this.destroyed && request === this.propertyFocusRequest &&
        flashFocus(document.querySelector(`[data-qa-options-pane="${this.question.id}"]`)), 0));
    }
    this.ref.markForCheck();
  }

  /** Resolves the clicked answer or matrix axis within this preview only. */
  private getPreviewFocusTarget(event: Event): string {
    const element = event.target instanceof Element ? event.target.closest('[data-qa-option-focus]') : null;
    const preview = event.currentTarget;
    return element && preview instanceof Element && preview.contains(element)
      ? element.getAttribute('data-qa-option-focus') : null;
  }

  /** Holds the visible text stable during typing while keeping the saved model current. */
  public onPreviewContentChange(field: 'title' | 'description', html: string): void {
    if (!this.pendingPreview || this.pendingPreview.locale !== this.locale) {
      this.pendingPreview = {
        locale: this.locale,
        title: this.question.title[this.locale],
        description: this.question.description.text[this.locale]
      };
    }
    if (field === 'title') {
      this.question.title[this.locale] = html;
    } else {
      this.question.description.text[this.locale] = html;
    }
    clearTimeout(this.previewRefreshTimer);
    this.previewRefreshTimer = setTimeout(() => this.refreshPreview(), 1000);
  }

  /** Uses the current locale even when it changes during a pending refresh. */
  public get previewTitle(): string {
    return this.pendingPreview?.locale === this.locale ? this.pendingPreview.title : this.question.title[this.locale];
  }

  public get previewDescription(): string {
    return this.pendingPreview?.locale === this.locale ? this.pendingPreview.description : this.question.description?.text?.[this.locale];
  }

  /** Flushes the pending preview refresh when editing leaves the field. */
  public refreshPreview(): void {
    clearTimeout(this.previewRefreshTimer);
    this.previewRefreshTimer = null;
    this.pendingPreview = null;
    if (!this.destroyed) {
      this.ref.markForCheck();
    }
  }

  /** Refreshes the document card when a control in the external panel changes. */
  public markPreviewForCheck(): void {
    this.ref.markForCheck();
  }

  /** Exposes the selected state without storing a second question model. */
  public get selected(): boolean {
    return this.inspector.selection.value?.template === this.properties;
  }

  /**
   * Unsubscribe from all subscriptions
   */
  public ngOnDestroy() {
    this.previewResizeObserver?.disconnect();
    this.stopNearWatch && this.stopNearWatch();
    clearTimeout(this.previewRefreshTimer);
    this.destroyed = true;
    this.inspector.release(this.properties);
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  /**
   * Builds the froala options object for the title editor
   *
   * @returns {any}
   */
  private buildTitleEditorOptions(): any {
    const self = this;
    return {
      key: FROALA_LICENSE_KEY,
    immediateAngularModelUpdate: true,
      events : {
        'paste.beforeCleanup': preventMultilineText,
        'initialized': function() {
          self.froalaInstance = this;
        },
        'destroy': function() {
          self.froalaInstance = null;
        },
        'keydown': (e: KeyboardEvent) => {
          if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            this.onChildFocus.emit();
            return false;
          }
        }
      },
      pluginsEnabled: ['wordPaste'],
      attribution: false,
      toolbarButtons: ['bold', 'italic', 'underline'],
      placeholderText: this._(this.getTitlePlaceholderKey()),
      quickInsertButtons: [],
      wordPasteModal: false,
      pastePlain: true,
      quickInsertTags: [],
      charCounterCount: false,
      toolbarInline: false,
      toolbarSticky: false,
      heightMin: 48,
      multiLine: false,
      language: 'de',
    };
  }

  /**
   * Focuses on title froala editor
   */
  public onFocusTitle = () => {
    const editorInstance = this.froalaInstance;
    if (!editorInstance) {
      // title editor might not be initialized yet (lazy froala). activating it also sets the focus
      this.titleEditor && this.titleEditor.activate();
      return;
    }

    editorInstance.events.focus();
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(editorInstance.el);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  /**
   * Triggers the "element position change" event in order to move the question
   * to the given position
   *
   * @param {number} newPosition
   */
  public onPositionChange(newPosition: number) {
    if (!Number.isInteger(newPosition) || newPosition < 0) {
      return;
    }
    this.state.changeElementPosition(this.position, newPosition);
  }

  /**
   * Triggers the "questionnaire image position change" event in order to move an image
   * to the given position
   *
   * @param {number} currentPosition
   * @param {number} newPosition
   */
  public onMediaPositionChange(currentPosition: number, newPosition: number, media: string) {
    // get medias
    const medias = (this.question && this.question.assets) ? this.question.assets[media] : null;

    if (!medias) {
      return;
    }

    // early bailout when changing positions does not make sense
    if (currentPosition >= (medias.length) || newPosition < 0) {
      return;
    }

    // reorder media by current and new position
    medias.splice(newPosition, 0, medias.splice(currentPosition, 1)[0]);
    this.triggerChangeDetection();
  }

  /**
   * Triggers the "element delete" event in order to delete this question
   */
  public onDelete() {
    this.editor.confirmDelete(() => this.state.deleteElement(this.position));
  }

  /**
   * Hides other "add element" popover elements and shows "add element" popover
   * for this question.
   *
   * @param popover
   */
  public onAddElement(popover: NgbPopover) {
    $('ngb-popover-window').hide();

    // wait a moment, so we don't hide the new popover
    setTimeout(() => {
      // hack to always open popover
      popover.close();
      popover.autoClose = false;
      // the popover hands itself to its template, so the template can close it from both card layouts
      popover.open({popover});

      // scroll to bottom when this question is the last element in the questionnaire
      // this way the popover will be fully visible, as it opens on bottom left position
      if (this.state.isOnLastPosition(this.position)) {
        const pane = document.querySelector<HTMLElement>('.qa-app-layout .qa-document-scroll');
        pane ? pane.scrollTo({top: pane.scrollHeight, behavior: 'smooth'})
          : window.scrollTo({top: document.body.scrollHeight, behavior: 'smooth'});
      }
    }, 100);
  }

  /**
   * Properties for move-to-page popover
   */
  public moveTargetPageIndex = 0;
  public moveInsertPosition: 'start' | 'end' = 'end';
  public moveJumpToPage = true;
  public movePageSelectOptions = {width: '100%', placeholder: '...'};

  /**
   * Opens the move-to-page popover
   *
   * @param popover The NgbPopover instance
   */
  public onMoveToPage(popover: NgbPopover) {
    $('ngb-popover-window').hide();

    // initialize with a different page (next or previous)
    if (this.currentPageIndex < this.pages.length - 1) {
      this.moveTargetPageIndex = this.currentPageIndex + 1;
    } else if (this.currentPageIndex > 0) {
      this.moveTargetPageIndex = this.currentPageIndex - 1;
    } else {
      this.moveTargetPageIndex = 0;
    }

    setTimeout(() => {
      popover.close();
      popover.autoClose = false;
      popover.open({popover});
    }, 100);
  }

  /**
   * Executes the move-to-page action
   *
   * @param popover The NgbPopover instance to close after move
   */
  public async executeMoveToPage(popover: NgbPopover): Promise<void> {
    if (!await confirm(this._(KEYS.EDITOR.CONFIRM_MOVE_ELEMENT))) {
      return;
    }

    const targetPage = this.pages[this.moveTargetPageIndex];
    if (!targetPage) {
      return;
    }

    // calculate target position
    let newPosition: number;
    if (this.moveInsertPosition === 'start') {
      newPosition = targetPage.startIndex;
    } else {
      // end of page = before the pageBreak (if exists) or at endIndex
      const pageBreakOffset = targetPage.endIndex < this.state.getQuestionnaire().questions.length &&
        this.state.getQuestionnaire().questions[targetPage.endIndex]?.format === 'pageBreak' ? 0 : 1;
      newPosition = targetPage.endIndex - pageBreakOffset;
    }

    // adjust for the element being removed from its current position
    if (this.position < newPosition) {
      newPosition--;
    }

    // move the element
    this.state.changeElementPosition(this.position, newPosition);

    // emit event to notify parent to jump to page if needed
    if (this.moveJumpToPage) {
      this.state.onMoveToPage.emit(this.moveTargetPageIndex);
    }

    alert(this._(KEYS.EDITOR.ELEMENT_MOVED_SUCCESSFULLY));
  }

  /**
   * Opens media center modal
   *
   * @param mediaType
   * @param containerAsset
   */
  public onOpenMediaCenterModal(
    mediaType: string,
    containerAsset?: Questionnaire.Container.Asset
  ) {
    !this.question.assets && (this.question.assets = {});
    !this.question.assets[mediaType] && (this.question.assets[mediaType] = []);

    const locale = this.locale;
    const questionnaireId =  this.state.getQuestionnaire().id;
    const buildAsset = this.mediaCenter.buildContainerAsset;
    const editMode = !!containerAsset;

    const callback = (mediaCenterAsset: MediaCenter.Asset) => {
      const list = (this.question.assets as any)[mediaType] as any[];
      (!editMode && mediaCenterAsset) && list.push(buildAsset({mediaCenterAsset, containerAsset: <any>containerAsset, locale}));
      (editMode && mediaCenterAsset) && buildAsset({mediaCenterAsset, containerAsset: <any>containerAsset, locale});
      this.triggerChangeDetection();
    };

    this.mediaCenter.openMediaCenter({
      questionnaireId, mediaType: <any>mediaType, locale, containerAsset: <any>containerAsset, callback
    });
  }

  /**
   * Deletes media by position and media type
   *
   * @param position
   * @param mediaType
   */
  public onDeleteMedia(position: number, mediaType: string) {
    this.editor.confirmDelete(() => {
      if (!this.question || !this.question.assets || !this.question.assets[mediaType]) {
        return;
      }

      this.question.assets[mediaType].splice(position, 1);
      this.triggerChangeDetection();
    });
  }

  /**
   * Opens question tab by given tab id
   *
   * @param el
   * @param cls
   * @param tab
   */
  public openTab(el: HTMLElement, cls: string, tab: string) {
    this.domHelper.addClasses(el, [cls]);
    this.selectPropertyTab(tab);
    this.selectQuestion();
  }

  /**
   * Alignment options for image assets
   */
  public assetAlignmentOptions = [
    {value: 'left', label: 'CHART_ALIGNMENT_LEFT'},
    {value: 'center', label: 'CHART_ALIGNMENT_CENTER'},
    {value: 'right', label: 'CHART_ALIGNMENT_RIGHT'}
  ];

  /**
   * Sets or removes the widthPercent display option on given image asset
   *
   * @param {Questionnaire.Container.Asset} asset
   * @param {number | null} value
   */
  public onAssetWidthPercentChange(asset: Questionnaire.Container.Asset, value: number | null) {
    !asset.options && (asset.options = {});

    if (value && value >= 1 && value <= 100) {
      asset.options.widthPercent = value;
    } else {
      delete asset.options.widthPercent;
    }

    this.triggerChangeDetection();
  }

  /**
   * Sets or removes the alignment display option on given image asset
   *
   * @param {Questionnaire.Container.Asset} asset
   * @param {string} value
   */
  public onAssetAlignmentChange(asset: Questionnaire.Container.Asset, value: string) {
    !asset.options && (asset.options = {});

    if (value === 'center' || value === 'right') {
      asset.options.alignment = value;
    } else {
      delete asset.options.alignment;
    }

    this.triggerChangeDetection();
  }

  /**
   * Sets or removes the fullWidth display option on given image asset
   *
   * @param {Questionnaire.Container.Asset} asset
   * @param {boolean} value
   */
  public onAssetFullWidthChange(asset: Questionnaire.Container.Asset, value: boolean) {
    !asset.options && (asset.options = {});

    if (value) {
      asset.options.fullWidth = true;
    } else {
      delete asset.options.fullWidth;
    }

    this.triggerChangeDetection();
  }

  /**
   * Triggers change detection
   */
  public triggerChangeDetection() {
    this.ref.detectChanges();
    this.ref.markForCheck();
    this.fixVideoPreviews();
  }

  private isEmailQuestion(): boolean {
    return !!this.question && this.question.extra === 'email';
  }

  public getTitlePlaceholderKey(): string {
    return this.isEmailQuestion() ? KEYS.EDITOR.ENTER_SUBJECT : KEYS.EDITOR.ENTER_TITLE;
  }

  public getDescriptionPlaceholderKey(): string {
    return this.isEmailQuestion() ? KEYS.EDITOR.ENTER_MAIL_TEXT : KEYS.EDITOR.ENTER_DESCRIPTION;
  }

  public getDescriptionLabelKey(): string {
    return this.isEmailQuestion() ? KEYS.EDITOR.MAIL_TEXT : KEYS.EDITOR.DESCRIPTION;
  }

  /**
   * Bugfix for google chrome. Forces the browser to reload the video poster images
   *
   * @param files
   */
  public fixVideoPreviews(waitMs = 10) {
    if (!this.question.assets || !this.question.assets.videos) {
      return;
    }
    for (const file of this.question.assets.videos) {
      const options = file.options;
      if (!options || !options.previewImage) {
        continue;
      }
      const previewImage = options.previewImage!;
      const preview = previewImage[this.locale] ? previewImage[this.locale] : null;

      if (preview) {
        options.previewImage[this.locale] = '';

        this.ref.detectChanges();
        this.ref.markForCheck();

        setTimeout(() => {
          previewImage[this.locale] = preview;

          this.ref.detectChanges();
          this.ref.markForCheck();
        }, waitMs);
      }
    }
  }

  public hasImages(): boolean {
    return !!this.value.get(this.question, ['assets', 'images', 'length']);
  }

  public hasVideos(): boolean {
    return !!this.value.get(this.question, ['assets', 'videos', 'length']);
  }

  public hasAudio(): boolean {
    return !!this.value.get(this.question, ['assets', 'audio', 'length']);
  }

  public hasMedia(): boolean {
    return this.hasImages() || this.hasVideos() || this.hasAudio();
  }

  /**
   * Returns true if this question type supports the hide-in-app option
   */
  public get supportsHideInApp(): boolean {
    const f = this.question.format;
    return f === 'info' || f === 'netDiagram'
      || f === 'barChart' || f === 'columnChart'
      || f === 'lineChart' || f === 'pieChart';
  }

  public hasConditions(): boolean {
    return !!this.value.get(this.question, ['conditions', 'show', 'length'])
      || !!this.value.get(this.question, ['conditions', 'expression', ]);
  }

  public hasVariables(): boolean {
    // @ts-ignore
    return this.variables.getVariableScopeNames().reduce((hasVariables, scope) => hasVariables = (!!this.variables.getMappedVariableName(scope, this.question.pathHash) || hasVariables), false);
  }
}
